import { Component } from '@theme/component';
import { debounce, isDesktopBreakpoint, prefersReducedMotion } from '@theme/utilities';

/**
 * Duración del deslizamiento de un paso, en ms. Tiene que coincidir con la transición declarada en
 * `snippets/product-carousel.liquid` — el JS la usa para saber cuándo terminó el paso.
 */
const STEP_MS = 650;

/** Píxeles de movimiento a partir de los cuales un arrastre deja de contar como clic. */
const DRAG_THRESHOLD = 5;

/** Cuánto se espera después de soltar antes de que el avance automático vuelva a correr. */
const RESUME_DELAY_MS = 1200;

/** Fricción de la inercia por fotograma (a 60fps). Más bajo = frena antes. */
const FRICTION = 0.94;

/** Debajo de esta velocidad (px/fotograma) la inercia se da por terminada y se encaja. */
const MIN_VELOCITY = 0.4;

/**
 * Fracción de una tarjeta que asoma cortada a cada lado del viewport, para que se note que el
 * carrusel sigue para los dos costados. Es un corrimiento puramente visual (ver `#peekPx`), nunca
 * se suma a `#position` — ese valor sigue moviéndose en múltiplos exactos de `(cardWidth + gap)`
 * desde cero, o el encaje de la inercia y el salto de foco (que redondean al múltiplo más cercano
 * DESDE cero) lo borrarían en la primera interacción.
 */
const PEEK_FRACTION = 0.25;

/**
 * El asomo en móvil, donde se muestra una sola tarjeta por vez. Es mayor que el de escritorio a
 * propósito: con una tarjeta a la vista, un asomo chico no alcanza para que se lea como carrusel.
 */
const MOBILE_PEEK_FRACTION = 0.35;

/**
 * Ancho de una tarjeta del carrusel: `columns` enteras más el asomo de los dos costados caben en la
 * ventana. Es la ÚNICA fórmula: la usa el carrusel armado y la tarjeta sola (`--pc-solo-width`), para
 * que la sola mida lo mismo que cualquiera del carrusel lleno y no cambie de tamaño al quedar una.
 *
 * @param {number} viewportWidth
 * @param {number} columns
 * @param {number} gap
 * @param {number} peekFraction
 */
const cardWidthFor = (viewportWidth, columns, gap, peekFraction) =>
  (viewportWidth - columns * gap) / (columns + 2 * peekFraction);

/**
 * Convierte una grilla de productos en un carrusel infinito. En escritorio siempre; en móvil sólo
 * si la instancia lo pide con `data-mobile-carousel` (la portada no lo pide, porque ahí móvil es una
 * grilla de 2 columnas; el cierre de la ficha sí).
 *
 * Por qué un componente propio y no `<slideshow-component>` (`assets/slideshow.js`), que ya existe
 * y ya sabe hacer carruseles: ese componente avanza de a `visibleSlides.length` (4 productos por
 * paso acá, no 1), su transición de loop sin costura está condicionada a `visibleSlides.length === 1`
 * — con 4 columnas rebobina de un salto visible — y su arrastre hace `clamp(0, slides.length - 1)`
 * ignorando el atributo `infinite`, así que nunca da la vuelta. Parchearlo no es opción: lo comparten
 * el hero, la galería de fotos DENTRO de cada tarjeta de producto, las recomendaciones, las listas de
 * colección y el buscador predictivo.
 *
 * Mecanismo: la pista se clona en sets completos hasta cubrir la ventana más un set de sobra, y la
 * posición se mantiene siempre normalizada en `[0, anchoSet)`. Como el contenido en `p` y en
 * `p - anchoSet` es idéntico píxel a píxel, restar un set es invisible — eso es todo el "infinito",
 * y es también lo que hace que el arrastre no tenga tope en ninguna dirección. El único cuidado es
 * que ese salto ocurra con la transición apagada, nunca a mitad de una.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport
 *
 * @extends Component<Refs>
 */
class ProductCarousel extends Component {
  /**
   * Sólo `viewport`, que lo emite nuestro propio snippet. La pista es `.resource-list`, que sale de
   * `snippets/resource-list.liquid` — compartido por cuatro secciones más, así que no se le agrega
   * un `ref`: se busca por selector y, si no está, el componente se retira sin romper nada. Ojo con
   * ampliar esta lista: `requiredRefs` LANZA dentro de `connectedCallback` antes de que corra una
   * sola línea propia, y el componente queda muerto.
   */
  requiredRefs = ['viewport'];

  /** @type {HTMLElement | null} */
  #track = null;

  /** Posición actual de la pista, en px, siempre normalizada a `[0, #setWidth)`. */
  #position = 0;

  #cardWidth = 0;
  #gap = 0;
  #setWidth = 0;
  #realCount = 0;

  /** Corrimiento visual constante para el peek (ver `PEEK_FRACTION`), en px. Sólo se usa en `#apply`. */
  #peekPx = 0;

  /** Ancho de la ventana con el que se hizo la última medición, para ignorar resizes verticales. */
  #measuredWidth = 0;

  /**
   * Observa el ANCHO REAL de la ventana del carrusel, no el de la pantalla.
   *
   * REPORTADO: con el cajón del carrito abierto y recargando, el carrusel aparecía mostrando más
   * de 4 productos. Causa: abrir o cerrar un cajón mueve `.page-wrapper` 480px (le pone un
   * `margin-right`) pero NO cambia `window.innerWidth` y NO dispara `resize`. Si la primera
   * medición cae con el cajón abierto, el ancho de tarjeta queda calculado para una página de 960
   * y al cerrarse la fila sigue con esas tarjetas chicas: en 1900px de pantalla entran ocho.
   *
   * Un `ResizeObserver` sobre la ventana sí lo ve, porque su ancho lo decide el layout de la
   * página. No hay riesgo de bucle: la ventana llena a su padre y la pista de adentro va en
   * `width: max-content` dentro de un `overflow: hidden`, así que cambiar el ancho de las tarjetas
   * no cambia el de la ventana.
   * @type {ResizeObserver | null}
   */
  #resizeObserver = null;

  /** @type {number | undefined} */
  #timer;

  /** @type {number | undefined} */
  #resumeTimer;

  /** @type {number | undefined} */
  #settleTimer;

  /** @type {number | undefined} */
  #inertiaFrame;

  #active = false;
  #paused = false;
  #dragging = false;
  #dragMoved = 0;

  /** Si ya se pidió `setPointerCapture` para este gesto (o sea, si ya es un arrastre de verdad). */
  #captured = false;

  /** @type {{ x: number, t: number }[]} */
  #samples = [];

  #dragStartX = 0;
  #dragStartPosition = 0;
  #velocity = 0;

  /**
   * Vigila que nadie le saque a un carrusel ARMADO lo que lo arma, y lo rehace si pasa.
   *
   * REPORTADO como «a veces no se arma» (≈1 de cada 8 cargas de los recomendados de la ficha,
   * medido en vivo). Causa: la sección se vuelve a pedir y el morph escribe el HTML del servidor
   * encima — que no trae `data-pc-active` ni las copias —; los quita, pero NO llama a
   * `updatedCallback` en este caso (en las corridas que fallaban había dos escrituras seguidas en el
   * mismo milisegundo, sin ninguna llamada). El componente quedaba vivo, con `#active` en true,
   * y sin pista: tarjetas pegadas en una grilla, para siempre. Con el refresco de los recomendados
   * por cambios del carrito el morph pasa a ser lo normal, no la excepción.
   * @type {MutationObserver | null}
   */
  #healObserver = null;

  #handleHeal = () => {
    // Sin armar (una sola tarjeta, móvil sin carrusel) no hay nada que curar.
    if (!this.#active) return;

    const track = this.refs.viewport?.querySelector('.resource-list') ?? null;
    const intact =
      track && track === this.#track && this.hasAttribute('data-pc-active') && track.querySelector('[data-pc-clone]');
    if (intact) return;

    // Un cuadro después, para que el morph termine de escribir antes de medir. Los cambios que
    // hace `#setup` por su cuenta (sacar y poner copias) llegan acá con todo ya armado y salen por
    // el `return` de arriba: no hay bucle.
    cancelAnimationFrame(this.#healFrame ?? 0);
    this.#healFrame = requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#track = this.refs.viewport?.querySelector('.resource-list') ?? null;
      if (this.#track) this.#setup();
    });
  };

  /** @type {number | undefined} */
  #healFrame;

  connectedCallback() {
    super.connectedCallback();

    this.#track = this.refs.viewport.querySelector('.resource-list');
    if (!this.#track) return;

    this.#setup();

    this.#healObserver?.disconnect();
    this.#healObserver = new MutationObserver(this.#handleHeal);
    this.#healObserver.observe(this, { attributes: true, attributeFilter: ['data-pc-active'] });
    this.#healObserver.observe(this.refs.viewport, { childList: true, subtree: true });

    window.addEventListener('resize', this.#handleResize);
    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this.refs.viewport);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    this.addEventListener('pointerdown', this.#handlePointerDown);
    this.addEventListener('pointermove', this.#handlePointerMove);
    this.addEventListener('pointerup', this.#handlePointerUp);
    this.addEventListener('pointercancel', this.#handlePointerUp);
    this.addEventListener('dragstart', this.#handleDragStart);
    // En captura: hay que llegar antes que el `<a>` de la tarjeta para poder cancelar el clic
    // que cierra un arrastre.
    this.addEventListener('click', this.#handleClick, true);
    document.addEventListener('visibilitychange', this.#handleVisibility);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#stop();
    this.#handleResize.cancel();
    this.#healObserver?.disconnect();
    this.#healObserver = null;
    cancelAnimationFrame(this.#healFrame ?? 0);
    clearTimeout(this.#resumeTimer);
    cancelAnimationFrame(this.#inertiaFrame ?? 0);

    window.removeEventListener('resize', this.#handleResize);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.removeEventListener('pointerleave', this.#handlePointerLeave);
    this.removeEventListener('focusin', this.#handleFocusIn);
    this.removeEventListener('focusout', this.#handleFocusOut);
    this.removeEventListener('pointerdown', this.#handlePointerDown);
    this.removeEventListener('pointermove', this.#handlePointerMove);
    this.removeEventListener('pointerup', this.#handlePointerUp);
    this.removeEventListener('pointercancel', this.#handlePointerUp);
    this.removeEventListener('dragstart', this.#handleDragStart);
    this.removeEventListener('click', this.#handleClick, true);
    document.removeEventListener('visibilitychange', this.#handleVisibility);
  }

  /**
   * La API de Section Rendering hace morph de secciones enteras cuando algo del carrito cambia, y
   * el HTML que manda el servidor trae sólo las tarjetas reales — o sea que borraría los clones y
   * dejaría la pista a un cuarto de largo. Rehacer el armado acá lo cura solo. El morph saltea los
   * subárboles que no cambiaron, así que esto no se dispara de gusto.
   */
  updatedCallback() {
    super.updatedCallback?.();

    this.#track = this.refs.viewport?.querySelector('.resource-list') ?? null;
    if (this.#track) this.#setup();
  }

  /**
   * Mide, clona y arranca. Es idempotente a propósito: `connectedCallback` puede correr más de una
   * vez (mover el nodo, re-render del editor) y un morph puede llamarlo desde `updatedCallback`.
   */
  #setup() {
    const track = this.#track;
    if (!track) return;

    this.#stop();

    // Se desactiva PRIMERO y se reactiva sólo al final, con todo listo. Cualquier salida temprana
    // de las de más abajo (móvil, sin productos, ventana todavía sin medir) tiene que dejar una
    // grilla común y corriente, no un estado a medias: `data-pc-active` es lo que enciende el CSS
    // de la pista, y dejarlo puesto sin clones ni `transform` deja las tarjetas descolocadas.
    this.#active = false;
    this.removeAttribute('data-pc-active');
    track.style.removeProperty('transform');
    track.style.removeProperty('transition-duration');
    this.style.removeProperty('--pc-solo-width');

    // Los clones se sacan SIEMPRE antes de medir: si no, la segunda pasada mediría la pista ya
    // llena y multiplicaría las copias en cada llamada.
    for (const clone of track.querySelectorAll('[data-pc-clone]')) clone.remove();

    // Escritorio siempre; móvil sólo si la instancia lo pide.
    //
    // En la portada, móvil es una grilla de 2 columnas y convertirla en carrusel sería una
    // regresión, así que el default sigue siendo "sólo escritorio". El cierre de la ficha sí lo
    // quiere en los dos, y lo pide con `data-mobile-carousel`. El CSS del snippet acompaña con la
    // misma bifurcación.
    const mobile = !isDesktopBreakpoint();
    if (mobile && !this.hasAttribute('data-mobile-carousel')) return;

    const items = /** @type {HTMLElement[]} */ ([...track.children]);
    this.#realCount = items.length;
    if (this.#realCount === 0) return;

    // Con una sola tarjeta no hay loop posible: clonarla y hacerla girar sería el mismo producto
    // dando vueltas. Se sale sin activar y una regla del snippet la deja centrada y quieta. Es el
    // mismo criterio que usa el carrusel de reels, que tiene la misma guarda.
    //
    // Con `data-hold-size` (los recomendados de la ficha) la tarjeta mide siempre lo mismo, sin
    // importar cuántos productos devuelva Shopify: las columnas salen de `data-reference-columns` y
    // no de `data-columns`, que la sección ya acotó a lo que hay. Antes se achicaban y la tarjeta
    // cambiaba de tamaño (a 1440px: 566px con 2, 401 con 3 y con 1). Con 2 productos el set no
    // llena la ventana y se clona hasta cubrirla: ven duplicados, como en cualquier carrusel
    // infinito. NO subir el piso para girar (se probó: con 2 quedaban quietas y se perdía el loop).
    //
    // Antes de salir (1 sola) se le publica el ancho que tendría en el carrusel lleno, con esas
    // mismas columnas: sin esto la tarjeta caía en el 340px de fábrica del CSS y se veía más chica.
    const referenceColumns = Math.max(
      1,
      Number(mobile ? this.dataset.mobileColumns : this.dataset.referenceColumns) || 1
    );
    if (this.#realCount < 2) {
      const width = this.refs.viewport.clientWidth;
      if (width === 0) return;

      const soloWidth = cardWidthFor(
        width,
        referenceColumns,
        Math.max(0, Number(this.dataset.gap) || 0),
        mobile ? MOBILE_PEEK_FRACTION : PEEK_FRACTION
      );
      if (soloWidth > 0) this.style.setProperty('--pc-solo-width', `${soloWidth}px`);
      return;
    }

    const requestedColumns = mobile
      ? this.dataset.mobileColumns
      : this.hasAttribute('data-hold-size')
        ? this.dataset.referenceColumns
        : this.dataset.columns;
    const columns = Math.max(1, Number(requestedColumns) || this.#realCount);
    const viewportWidth = this.refs.viewport.clientWidth;
    if (viewportWidth === 0) return;

    this.#measuredWidth = viewportWidth;
    this.#gap = Math.max(0, Number(this.dataset.gap) || 0);

    // El ancho de tarjeta lo calcula el JS y el CSS sólo lo consume, para que el paso de la
    // animación y el ancho real no puedan desincronizarse nunca: son el mismo número. Cada tarjeta
    // ocupa `cardWidth + gap` en el patrón que se repite, incluida la última antes del empalme —
    // por eso el gap se descuenta `columns` veces y no `columns - 1`. El divisor se infla con
    // `PEEK_FRACTION` de sobra a cada lado para que entren un poco más de `columns` tarjetas en el
    // mismo ancho medido, dejando lugar para el corte del peek en `#apply`.
    // En móvil el asomo es MÁS GRANDE a propósito: con una sola tarjeta a la vista, un asomo chico
    // no se lee como carrusel — hay que ver medio producto a cada lado para que se entienda que
    // sigue. En escritorio, con 4 enteras, esa misma proporción sería ruido lateral.
    const peekFraction = mobile ? MOBILE_PEEK_FRACTION : PEEK_FRACTION;
    this.#cardWidth = cardWidthFor(viewportWidth, columns, this.#gap, peekFraction);
    this.#setWidth = this.#realCount * (this.#cardWidth + this.#gap);
    // El offset de render sale de DESPEJAR el asomo, no de una fracción de tarjeta.
    //
    // La ventana muestra `columns` tarjetas enteras con un asomo a cada lado, o sea `columns + 1`
    // separaciones. Despejando ese asomo:
    //
    //     asomo = (ancho - columns * cardWidth - (columns + 1) * gap) / 2
    //
    // y el offset es lo que hay que meterse dentro de la primera tarjeta para que muestre
    // justamente eso: `cardWidth - asomo`.
    //
    // Antes era `(1 - PEEK_FRACTION) * (cardWidth + gap)`, que ignora el gap del borde y deja el
    // asomo izquierdo más chico que el derecho (medido a 1919px: 83 contra 95). Con el despeje los
    // dos dan el mismo número por construcción, que es lo que se pidió: las tarjetas centradas.
    //
    // Sigue siendo un corrimiento PURAMENTE VISUAL: no se suma a `#position`, que tiene que
    // seguir moviéndose en múltiplos exactos de `(cardWidth + gap)` desde cero o el encaje de la
    // inercia y el salto de foco lo borrarían en la primera interacción.
    //
    // El `max(0, ...)` es la red para el caso en que el gap sea tan grande que las `columns`
    // enteras más sus separaciones ya no entren: ahí no hay asomo posible y la ventana arranca
    // pegada al borde de la primera tarjeta entera, en vez de invertir el corrimiento.
    const sidePeek = Math.max(
      0,
      (viewportWidth - columns * this.#cardWidth - (columns + 1) * this.#gap) / 2
    );
    this.#peekPx = this.#cardWidth - sidePeek;
    if (this.#setWidth <= 0) return;

    this.style.setProperty('--pc-card-width', `${this.#cardWidth}px`);
    this.style.setProperty('--pc-gap', `${this.#gap}px`);

    // Con la posición normalizada en `[0, setWidth)`, la ventana nunca necesita mirar más allá de
    // `setWidth + viewportWidth`. Se suma un set extra de colchón contra el redondeo subpíxel.
    const sets = 2 + Math.ceil(viewportWidth / this.#setWidth);
    for (let set = 1; set < sets; set++) {
      for (const item of items) {
        track.appendChild(this.#cloneItem(item));
      }
    }

    this.#active = true;
    this.setAttribute('data-pc-active', '');
    // Se ENCAJA al paso nuevo antes de normalizar. `#position` está en píxeles del ancho de tarjeta
    // con el que se midió la vez anterior; si el ancho cambió —al abrir o cerrar un cajón, que
    // mueve la página 480px— ese número ya no es múltiplo del paso y la fila queda parada a mitad
    // de camino para siempre: se ven `columns - 1` tarjetas enteras y dos pedazos, en vez de las
    // `columns` que pide el ajuste. Redondear al múltiplo más cercano la devuelve a una posición
    // de reposo. En la primera medición `#position` es 0 y esto no hace nada.
    const paso = this.#cardWidth + this.#gap;
    if (paso > 0) this.#position = Math.round(this.#position / paso) * paso;
    this.#position = ((this.#position % this.#setWidth) + this.#setWidth) % this.#setWidth;
    this.#apply(false);
    this.#start();
  }

  /**
   * @param {HTMLElement} item
   * @returns {HTMLElement}
   */
  #cloneItem(item) {
    const clone = /** @type {HTMLElement} */ (item.cloneNode(true));
    clone.setAttribute('data-pc-clone', '');
    clone.setAttribute('aria-hidden', 'true');

    // Cada tarjeta trae tres `id` (la propia `<product-card>`, el enlace y el formulario de compra
    // rápida). Verificado en el DOM real que nada los referencia (ni `for`, ni `aria-labelledby`,
    // ni `form=`), así que se pueden sacar sin romper nada — y hay que sacarlos, porque doce copias
    // dejarían doce ids repetidos.
    if (clone.hasAttribute('id')) clone.removeAttribute('id');
    for (const el of clone.querySelectorAll('[id]')) el.removeAttribute('id');

    // Los clones se ven y se pueden clicar (en un loop infinito, la mitad de lo que hay en pantalla
    // son clones), pero quedan fuera del recorrido del tabulador: con `aria-hidden` puesto, dejar
    // algo enfocable adentro es un error de accesibilidad. El teclado recorre sólo las 4 reales.
    for (const el of clone.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')) {
      el.setAttribute('tabindex', '-1');
    }

    return clone;
  }

  #start() {
    this.#stop();
    if (!this.#active || prefersReducedMotion()) return;

    const seconds = Number(this.dataset.interval) || 4;
    this.#timer = setInterval(this.#tick, seconds * 1000);
  }

  #stop() {
    clearInterval(this.#timer);
    this.#timer = undefined;
  }

  #tick = () => {
    if (!this.#active || this.#paused || this.#dragging || document.hidden) return;
    this.#step(1);
  };

  /**
   * Avanza `count` tarjetas con transición.
   *
   * El salto de módulo va DESPUÉS de que termina el deslizamiento, nunca antes. Hacerlo antes fue
   * un bug real y medido: restarle un set a la posición la deja NEGATIVA, y una posición negativa
   * es pista que no existe — el `transform` se vuelve positivo (medido: +323px), la pista se corre
   * a la derecha y durante los 650ms de la transición queda un hueco en blanco pegado al borde
   * izquierdo. Dejando que la posición pase de largo hasta `setWidth` y normalizando al final, todo
   * lo que se muestra cae siempre dentro de la pista clonada.
   *
   * @param {number} count
   */
  #step(count) {
    if (!this.#track) return;

    this.#position += count * (this.#cardWidth + this.#gap);
    this.#apply(true);
    this.#scheduleSettle();
  }

  /**
   * Al terminar el deslizamiento devuelve la posición al rango `[0, setWidth)` sin transición. Es
   * invisible porque el contenido en `p` y en `p - setWidth` es idéntico píxel a píxel — de eso se
   * trata todo el "infinito".
   */
  #scheduleSettle() {
    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      if (this.#dragging) return;
      const before = this.#position;
      this.#normalize();
      if (this.#position !== before) this.#apply(false);
    }, STEP_MS + 30);
  }

  /**
   * @param {boolean} animated
   */
  #apply(animated) {
    const track = this.#track;
    if (!track) return;

    track.style.transitionDuration = animated && !prefersReducedMotion() ? `${STEP_MS}ms` : '0ms';
    track.style.transform = `translate3d(${-(this.#position + this.#peekPx)}px, 0, 0)`;
  }

  /** Normaliza la posición a `[0, setWidth)`. Es lo único que hace falta para que no haya topes. */
  #normalize() {
    if (this.#setWidth <= 0) return;
    this.#position = ((this.#position % this.#setWidth) + this.#setWidth) % this.#setWidth;
  }

  #handleResize = debounce(() => {
    // Un resize sólo vertical es, en el móvil, la barra de direcciones apareciendo y desapareciendo.
    // Rehacer el armado ahí tira el carrusel al piso sin motivo — mismo criterio que `marquee.js`.
    //
    // Se compara contra el ancho de la VENTANA del carrusel, que es lo que se midió, y no contra
    // `window.innerWidth`: eran dos números distintos (la ventana descuenta márgenes de página), así
    // que la guarda nunca se cumplía y el armado se rehacía en cada resize vertical. Y al revés,
    // ahora sí detecta el caso que importa: la página se angosta o se ensancha 480px al abrir o
    // cerrar un cajón sin que `window.innerWidth` cambie ni un píxel.
    const ancho = this.refs.viewport?.clientWidth ?? 0;
    if (ancho === this.#measuredWidth && this.#active) return;
    this.#setup();
  }, 250);

  #handleVisibility = () => {
    if (document.hidden) this.#stop();
    else this.#start();
  };

  #handlePointerEnter = () => {
    this.#paused = true;
  };

  #handlePointerLeave = () => {
    this.#paused = false;
  };

  #handleFocusIn = (/** @type {FocusEvent} */ event) => {
    this.#paused = true;

    // Al tabular a una tarjeta real hay que traerla a la vista: puede estar fuera de la ventana,
    // y un foco invisible es peor que no tener foco.
    const item = /** @type {HTMLElement | null} */ (
      /** @type {HTMLElement} */ (event.target)?.closest?.('.resource-list__item')
    );
    if (!item || !this.#active || item.hasAttribute('data-pc-clone')) return;

    const track = this.#track;
    if (!track) return;

    const step = this.#cardWidth + this.#gap;

    // Siempre se avanza HACIA ADELANTE hasta la copia más cercana de esa tarjeta, nunca hacia
    // atrás: retroceder dejaría la posición negativa, o sea pista inexistente y un hueco en blanco
    // en el borde izquierdo (el mismo bug que se corrigió en `#step`).
    //
    // Se descuenta `#peekPx` del objetivo porque `#apply` ya lo suma al renderizar: sin este
    // descuento, la tarjeta enfocada caía siempre en la posición recortada del borde (sólo
    // PEEK_FRACTION de sí misma visible, foco casi invisible) en vez de quedar completa en
    // pantalla — medido en vivo con foco de teclado real, no en código.
    const forward =
      (((item.offsetLeft - this.#peekPx - this.#position) % this.#setWidth) + this.#setWidth) % this.#setWidth;

    this.#position = Math.round((this.#position + forward) / step) * step;
    this.#apply(true);
    this.#scheduleSettle();
  };

  #handleFocusOut = (/** @type {FocusEvent} */ event) => {
    if (this.contains(/** @type {Node | null} */ (event.relatedTarget))) return;
    this.#paused = false;
  };

  /**
   * Mata el arrastre NATIVO del navegador (drag & drop de HTML5) antes de que empiece.
   *
   * Las tarjetas son `<a>` con `<img>` adentro, y las dos cosas son arrastrables de fábrica. Al
   * apretar sobre una foto y mover, el navegador abre SU propio arrastre y, al hacerlo, **cancela
   * la secuencia de puntero**: llega un `pointercancel` y no llegan más `pointermove`. El carrusel
   * se queda con el primer movimiento, dispara la inercia con esa velocidad y pega un tirón, o no
   * se mueve. Encima el cursor pasa a ser el círculo rojo tachado de "no se puede soltar acá".
   *
   * Medido en vivo con arrastre real de mouse (la Actions API de WebDriver, porque un
   * `pointerdown` sintético NO dispara el arrastre nativo): de 11 movimientos enviados llegó
   * 1 `pointermove`, 1 `dragstart`, 1 `pointercancel` y 0 `pointerup`.
   *
   * **En Firefox pasa y en Chrome no** porque lo único que había contra esto era
   * `-webkit-user-drag: none` en el CSS, que es propietario de WebKit/Blink: Firefox lo ignora
   * (computa "no soportado" y el `img` sigue con `draggable === true`).
   *
   * El `pointer-events: none` que el CSS le pone a las tarjetas mientras se arrastra tampoco
   * alcanzaba: se aplica recién cuando el gesto supera el umbral, y `dragstart` es anterior.
   */
  #handleDragStart = (/** @type {DragEvent} */ event) => {
    event.preventDefault();
  };

  #handlePointerDown = (/** @type {PointerEvent} */ event) => {
    if (!this.#active || event.button !== 0) return;

    cancelAnimationFrame(this.#inertiaFrame ?? 0);
    clearTimeout(this.#resumeTimer);
    this.#stop();

    this.#dragging = true;
    this.#dragMoved = 0;
    this.#dragStartX = event.clientX;
    this.#dragStartPosition = this.#position;
    this.#velocity = 0;
    this.#samples = [{ x: event.clientX, t: event.timeStamp }];

    // NO se captura el puntero acá, y esa es la clave de que se pueda hacer clic en una tarjeta.
    // `setPointerCapture` redirige toda la secuencia del puntero al elemento que captura, y el
    // `click` que sale de esa secuencia se dispara sobre el capturador — nunca llega al `<a>` de
    // la tarjeta. Capturando en el `pointerdown` quedaba un carrusel que se arrastraba pero en el
    // que NINGÚN producto se podía abrir. La captura se pide recién cuando el gesto supera el
    // umbral y ya es inequívocamente un arrastre (ver `#handlePointerMove`).
    this.#captured = false;
  };

  /** Convierte el gesto en arrastre: recién acá se captura el puntero y se apagan los clics. */
  #beginDrag(/** @type {PointerEvent} */ event) {
    this.#captured = true;
    this.setAttribute('dragging', '');
    // Lanza `NotFoundError` si el puntero ya no está activo (se soltó fuera de la ventana, o el
    // evento es sintético). No es motivo para abortar: sin captura el arrastre igual funciona
    // mientras el cursor no se vaya del elemento.
    try {
      this.setPointerCapture(event.pointerId);
    } catch {
      /* sin captura, el arrastre igual funciona */
    }
  }

  #handlePointerMove = (/** @type {PointerEvent} */ event) => {
    if (!this.#dragging) return;

    const delta = event.clientX - this.#dragStartX;
    this.#dragMoved = Math.max(this.#dragMoved, Math.abs(delta));

    if (!this.#captured) {
      // Por debajo del umbral todavía puede ser un clic: no se toca nada, para no robarle el
      // evento al enlace de la tarjeta.
      if (this.#dragMoved <= DRAG_THRESHOLD) return;
      this.#beginDrag(event);
    }

    this.#position = this.#dragStartPosition - delta;
    this.#normalize();
    this.#apply(false);

    this.#samples.push({ x: event.clientX, t: event.timeStamp });
    if (this.#samples.length > 5) this.#samples.shift();
  };

  #handlePointerUp = (/** @type {PointerEvent} */ event) => {
    if (!this.#dragging) return;

    this.#dragging = false;
    this.removeAttribute('dragging');
    if (this.hasPointerCapture(event.pointerId)) this.releasePointerCapture(event.pointerId);

    // Nunca llegó a ser un arrastre: fue un clic. No hay que encajar la pista ni lanzar inercia —
    // un reacomodo acá movería la tarjeta justo cuando el usuario la está clickeando.
    if (!this.#captured) {
      this.#samples = [];
      this.#scheduleResume();
      return;
    }

    // Velocidad de las últimas muestras, no de todo el gesto: lo que importa es cómo venía la mano
    // al soltar, no el promedio del arrastre entero.
    const first = this.#samples[0];
    const last = this.#samples[this.#samples.length - 1];
    const elapsed = last && first ? last.t - first.t : 0;
    this.#velocity = elapsed > 0 ? (-(last.x - first.x) / elapsed) * 16 : 0;

    this.#samples = [];
    this.#runInertia();
  };

  /** Desliza con la velocidad de salida, frena por fricción y encaja en el múltiplo más cercano. */
  #runInertia() {
    const stepDistance = this.#cardWidth + this.#gap;

    const frame = () => {
      if (this.#dragging) return;

      this.#velocity *= FRICTION;

      if (Math.abs(this.#velocity) < MIN_VELOCITY) {
        this.#position = Math.round(this.#position / stepDistance) * stepDistance;
        this.#normalize();
        this.#apply(true);
        this.#scheduleResume();
        return;
      }

      this.#position += this.#velocity;
      this.#normalize();
      this.#apply(false);
      this.#inertiaFrame = requestAnimationFrame(frame);
    };

    this.#inertiaFrame = requestAnimationFrame(frame);
  }

  #scheduleResume() {
    clearTimeout(this.#resumeTimer);
    this.#resumeTimer = setTimeout(() => this.#start(), RESUME_DELAY_MS);
  }

  /**
   * Un arrastre termina en un `click` sobre la tarjeta que quedó bajo el cursor, y sin esto
   * arrastrar navegaría al producto. El umbral distingue el arrastre real del temblor de la mano
   * al hacer un clic normal, que sí tiene que pasar.
   */
  #handleClick = (/** @type {MouseEvent} */ event) => {
    if (this.#dragMoved <= DRAG_THRESHOLD) return;

    event.preventDefault();
    event.stopPropagation();
    this.#dragMoved = 0;
  };
}

if (!customElements.get('product-carousel-component')) {
  customElements.define('product-carousel-component', ProductCarousel);
}
